import { cx } from '@linaria/core';
import * as React from 'react';
import StorageAppLinkBundle from 'src/cms-components/StorageAppLinkBundle/StorageAppLinkBundle';
import { useIsMobileDevice } from 'src/hooks/useIsMobileDevice';
import { StorageHeroCardStyles } from './StorageHeroCardStyles';
import HeroBgSection from './components/HeroBgSection';
import HeroButtons from './components/HeroButtons';
import HeroCardWrapper from './components/HeroCardWrapper';
import HeroDesktopForgroundImage from './components/HeroDesktopForgroundImage';
import HeroText from './components/HeroText';
import { HeroDefaultSettings } from './types';

interface StorageCardProps {
  node: IStorageHeroCard;
}

// eslint-disable-next-line complexity
const StorageHeroCard: React.FC<StorageCardProps> = function ({ node: heroCard }) {
  const isMobileDevice = useIsMobileDevice();
  const settings = Object.assign(HeroDefaultSettings, heroCard?.behaviors || {});
  const variant = settings.entity_variant.hero_variant_type;
  const isQrHero = heroCard.relationships?.secondary_image?.field_category?.some(
    category => category === 'qr',
  );
  const hideQrCode = Boolean(isQrHero && isMobileDevice.current);
  const direction = settings.entity_variant?.content_position;
  const cmsTheme = settings.entity_variant?.theme;
  const cmsOverlayTheme = settings.entity_variant?.overlay;
  const foregroundImageAlignment = settings?.entity_variant?.foreground_image_alignment ?? 'left';
  const decorativeImages: Record<string, boolean> = {};
  const decorativeImagesSettings: Record<string, string> =
    settings?.entity_variant?.decorative_images ?? {};
  Object.keys(decorativeImagesSettings)?.forEach(key => {
    // This comes over as a string value, either "0" or the name of the property again.
    decorativeImages[key] = parseInt(decorativeImagesSettings[key], 10) !== 0;
  });

  const heroImage = heroCard.relationships.image?.relationships?.field_media_image?.gatsbyImage;
  const foregroundImage =
    heroCard?.relationships?.secondary_image?.relationships?.field_media_image.gatsbyImage;
  const logoImage = heroCard.relationships.logo;
  const appLinkBundle = heroCard.relationships.field_storage;

  const lazyLoad = settings?.entity_variant?.load_eagerly ? 'eager' : 'lazy';
  const styleSettings = React.useMemo(
    () => ({
      theme: cmsTheme,
      textDirection: direction,
      foregroundAlignment: foregroundImageAlignment,
      overlayTheme: cmsOverlayTheme,
      variant,
    }),
    [cmsTheme, direction, foregroundImageAlignment, cmsOverlayTheme, variant],
  );
  const { mainContainer, heroContent, imgContentContainer } = StorageHeroCardStyles(styleSettings);

  return (
    <div id={heroCard.component_id} data-id={heroCard.drupal_id} className={mainContainer()}>
      <div className="pds-bg-surface-layer/90 pds-sr-only" />
      <div className="pds-relative pds-h-full">
        <HeroBgSection
          hideQrCode={hideQrCode}
          heroCard={heroCard}
          heroImage={heroImage}
          lazyLoad={lazyLoad}
          foregroundImage={foregroundImage}
          styleSettings={styleSettings}
          decorativeImages={decorativeImages}
        />
        <div className={heroContent({ className: 'heroContent' })}>
          <div className={imgContentContainer({ className: 'pds-max-w-[1300px]' })}>
            {foregroundImage && !hideQrCode ? (
              <HeroDesktopForgroundImage
                heroCard={heroCard}
                lazyLoad={lazyLoad}
                foregroundImage={foregroundImage}
                styleSettings={styleSettings}
                isDecorative={decorativeImages.foreground}
              />
            ) : (
              <div className="pds-hidden pds-h-full pds-w-full lg:pds-block" />
            )}
            <HeroCardWrapper styleSettings={styleSettings}>
              <HeroText
                styleSettings={styleSettings}
                heroCard={heroCard}
                logoImage={logoImage}
                isLogoDecorative={decorativeImages.logo}
                lazyLoad={lazyLoad}
              >
                {heroCard.links.length > 0 && (
                  <div className="pds-mt-5">
                    <HeroButtons isQrHero={isQrHero} hideQrCode={hideQrCode} heroCard={heroCard} />
                  </div>
                )}

                {appLinkBundle && (
                  <StorageAppLinkBundle
                    node={appLinkBundle}
                    cmsTheme={cmsTheme}
                    bodyClassName={cx(variant === 'background_hero' && 'lg:pds-text-neutral-text')}
                    className="pds-mt-4.5"
                    lazyLoad={lazyLoad}
                    analyticsEvent={{
                      eventParams: {
                        component_type: 'hero',
                        content_name: `${settings.entity_heading.element} / ${heroCard.title}`,
                      },
                    }}
                  />
                )}
              </HeroText>
            </HeroCardWrapper>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StorageHeroCard;
